import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Inbox, User, ShieldOff, Send, Clock, CheckCircle2, AlertCircle, RefreshCw, ArrowLeft } from 'lucide-react';
import supportService, { SupportTicket } from '../../services/support.service';
import { API_BASE_URL } from '../../utils/apiClient';
import authService from '../../services/auth.service';
import CommonDropdown from '../Common/CommonDropdown';
import CommonPagination from '../Common/CommonPagination';
import styles from './SupportAdmin.module.css';

function formatDate(iso: string) {
    return new Date(iso).toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
}

function getInitials(name: string) {
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
}

function groupByUser(tickets: SupportTicket[]): { userId: string; userName: string; email: string | null; tickets: SupportTicket[] }[] {
    const map = new Map<string, { userId: string; userName: string; email: string | null; tickets: SupportTicket[] }>();
    for (const t of tickets) {
        if (!map.has(t.user_id)) {
            map.set(t.user_id, { userId: t.user_id, userName: t.user_name, email: t.user_email, tickets: [] });
        }
        map.get(t.user_id)!.tickets.push(t);
    }
    return Array.from(map.values());
}

const STATUS_OPTIONS = [
    { value: '', label: 'All Statuses' },
    { value: 'open', label: 'Open' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'resolved', label: 'Resolved' },
];

const SupportAdmin: React.FC = () => {
    const { userId } = useParams<{ userId: string }>();
    const navigate = useNavigate();
    const user = authService.getUser();
    const isSuperAdmin = user?.is_superuser;

    const [tickets, setTickets] = useState<SupportTicket[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
    const [totalItems, setTotalItems] = useState(0);
    const [pageSize, setPageSize] = useState(30);

    const [replyText, setReplyText] = useState('');
    const [sendingReply, setSendingReply] = useState(false);
    const [newStatus, setNewStatus] = useState('');
    const [updatingStatus, setUpdatingStatus] = useState(false);

    const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

    const loadTickets = useCallback(async (p: number, s: string, st: string, ps: number = pageSize) => {
        setLoading(true);
        try {
            const res = await supportService.getAllTickets(p, ps, {
                search: s || undefined,
                status: st || undefined,
                user_id: userId || undefined,
            });
            setTickets(res.items);
            setPage(res.page);
            setTotalItems(res.total);
            setTotalPages(Math.ceil(res.total / res.page_size));
        } catch {
            // silent
        } finally {
            setLoading(false);
        }
    }, [userId]);

    useEffect(() => {
        if (!isSuperAdmin) return;
        loadTickets(1, '', '', pageSize);
    }, [isSuperAdmin, loadTickets, pageSize]);

    const handleSearchChange = (val: string) => {
        setSearch(val);
        if (searchTimeout.current) clearTimeout(searchTimeout.current);
        searchTimeout.current = setTimeout(() => loadTickets(1, val, statusFilter, pageSize), 400);
    };

    const handleStatusFilterChange = (val: string) => {
        setStatusFilter(val);
        loadTickets(1, search, val, pageSize);
    };

    const handleSelectTicket = (ticket: SupportTicket) => {
        setSelectedTicket(ticket);
        setNewStatus(ticket.status);
        setReplyText('');
    };

    const handleSendReply = async () => {
        if (!selectedTicket || !replyText.trim()) return;
        setSendingReply(true);
        try {
            const reply = await supportService.replyToTicket(selectedTicket.id, replyText.trim());
            const updated = { ...selectedTicket, replies: [...(selectedTicket.replies || []), reply] };
            // If status was auto-updated to in_progress by backend
            if (selectedTicket.status === 'open') {
                updated.status = 'in_progress';
                setNewStatus('in_progress');
            }
            setSelectedTicket(updated);
            setTickets(prev => prev.map(t => t.id === updated.id ? updated : t));
            setReplyText('');
        } catch {
            // silent
        } finally {
            setSendingReply(false);
        }
    };

    const handleUpdateStatus = async () => {
        if (!selectedTicket || !newStatus) return;
        setUpdatingStatus(true);
        try {
            const updated = await supportService.updateTicketStatus(selectedTicket.id, newStatus);
            setSelectedTicket(updated);
            setTickets(prev => prev.map(t => t.id === updated.id ? updated : t));
        } catch {
            // silent
        } finally {
            setUpdatingStatus(false);
        }
    };

    if (!isSuperAdmin) {
        return (
            <div className={styles.accessDenied}>
                <ShieldOff size={48} />
                <p>Access Restricted</p>
                <span style={{ fontSize: 13 }}>Only Super Admins can view the Support dashboard.</span>
            </div>
        );
    }

    const groups = groupByUser(tickets);

    return (
        <div className={styles.adminPage}>
            {/* ── Master list ── */}
            <div className={styles.masterPane}>
                <div className={styles.masterPaneHeader}>
                    <div className={styles.masterPaneTitle}>
                        {userId && (
                            <button
                                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#6b7280', padding: 4, display: 'flex', alignItems: 'center' }}
                                onClick={() => navigate('/support-admin')}
                                title="Back to list"
                            >
                                <ArrowLeft size={16} />
                            </button>
                        )}
                        <Inbox size={20} />
                        {userId && tickets.length > 0 ? tickets[0].user_name : 'Support Tickets'}
                        <button
                            style={{ marginLeft: 'auto', background: 'transparent', border: 'none', cursor: 'pointer', color: '#6b7280', padding: 4 }}
                            onClick={() => loadTickets(page, search, statusFilter)}
                            title="Refresh"
                        >
                            <RefreshCw size={14} />
                        </button>
                    </div>
                    <div className={styles.filterRow}>
                        <input
                            className={styles.searchInput}
                            placeholder="Search tickets..."
                            value={search}
                            onChange={e => handleSearchChange(e.target.value)}
                        />
                        <div className={styles.statusSelectWrapper}>
                            <CommonDropdown
                                value={statusFilter}
                                onChange={handleStatusFilterChange}
                                options={STATUS_OPTIONS}
                                size="md"
                                placeholder="All Statuses"
                            />
                        </div>
                    </div>
                </div>

                <div className={styles.masterPaneBody}>
                    {loading ? (
                        <div className={styles.loadingState}>
                            <div className={styles.spinner} />
                            Loading tickets...
                        </div>
                    ) : groups.length === 0 ? (
                        <div className={styles.emptyMaster}>
                            <Inbox size={32} style={{ opacity: 0.3 }} />
                            <p>No tickets found</p>
                        </div>
                    ) : (
                        <>
                            {groups.map(group => (
                                <div key={group.userId} className={styles.userGroup}>
                                    <div className={styles.userGroupHeader}>
                                        <div className={styles.userAvatar}>{getInitials(group.userName)}</div>
                                        <div>
                                            <div>{group.userName}</div>
                                            {group.email && <div style={{ fontSize: 10, opacity: 0.6, fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>{group.email}</div>}
                                        </div>
                                        <div className={styles.ticketCount}>{group.tickets.length}</div>
                                    </div>
                                    {group.tickets.map(ticket => (
                                        <div
                                            key={ticket.id}
                                            className={`${styles.ticketRow} ${selectedTicket?.id === ticket.id ? styles.selected : ''}`}
                                            onClick={() => handleSelectTicket(ticket)}
                                        >
                                            <div className={styles.ticketRowContent}>
                                                <div className={styles.ticketRowSubject}>{ticket.subject}</div>
                                                <div className={styles.ticketRowMeta}>{ticket.category} · {formatDate(ticket.created_at)}</div>
                                            </div>
                                            <span className={`${styles.statusBadge} ${styles[ticket.status]}`}>
                                                {ticket.status.replace('_', ' ')}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </>
                    )}
                </div>

                {!loading && totalItems > 0 && (
                    <CommonPagination
                        show={true}
                        pageCount={totalPages}
                        currentPage={page - 1}
                        onPageChange={({ selected }) => loadTickets(selected + 1, search, statusFilter, pageSize)}
                        totalItems={totalItems}
                        itemsPerPage={pageSize}
                        onItemsPerPageChange={(size) => setPageSize(size)}
                    />
                )}
            </div>

            {/* ── Detail pane ── */}
            <div className={styles.detailPane}>
                {!selectedTicket ? (
                    <div className={styles.detailEmpty}>
                        <Inbox size={40} style={{ opacity: 0.2 }} />
                        <p>Select a ticket to view details and reply</p>
                    </div>
                ) : (
                    <>
                        <div className={styles.detailHeader}>
                            <div className={styles.detailMeta}>
                                <div className={styles.detailSubject}>{selectedTicket.subject}</div>
                                <div className={styles.statusActions}>
                                    <div className={styles.statusSelectWrapper2}>
                                        <CommonDropdown
                                            value={newStatus}
                                            onChange={setNewStatus}
                                            options={[
                                                { value: 'open', label: 'Open' },
                                                { value: 'in_progress', label: 'In Progress' },
                                                { value: 'resolved', label: 'Resolved' }
                                            ]}
                                            size="sm"
                                        />
                                    </div>
                                    <button
                                        className={styles.updateBtn}
                                        onClick={handleUpdateStatus}
                                        disabled={updatingStatus || newStatus === selectedTicket.status}
                                    >
                                        {updatingStatus ? <div className={styles.btnSpinner} style={{ display: 'inline-block' }} /> : 'Update'}
                                    </button>
                                </div>
                            </div>
                            <div className={styles.detailInfo}>
                                <span><User size={11} /> {selectedTicket.user_name}</span>
                                <span><Clock size={11} /> {formatDate(selectedTicket.created_at)}</span>
                                <span style={{ textTransform: 'capitalize' }}>
                                    {selectedTicket.status === 'resolved' ? <CheckCircle2 size={11} style={{ color: '#86efac' }} /> : <AlertCircle size={11} />}
                                    {selectedTicket.category}
                                </span>
                            </div>
                        </div>

                        <div className={styles.detailBody}>
                            <div className={styles.replyThread}>
                                {/* First message: The Original Ticket */}
                                {(selectedTicket.description || selectedTicket.image_urls?.length > 0) && (
                                    <div className={`${styles.replyRow} ${styles.replyTheirs}`}>
                                        <div className={styles.replyBubble}>
                                            {selectedTicket.description && <p>{selectedTicket.description}</p>}
                                            {selectedTicket.image_urls?.length > 0 && (
                                                <div className={styles.imageGrid}>
                                                    {selectedTicket.image_urls.map((url, i) => {
                                                        const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;
                                                        return (
                                                            <img
                                                                key={i}
                                                                src={fullUrl}
                                                                className={styles.thumb}
                                                                alt="attachment"
                                                                onClick={() => window.open(fullUrl, '_blank')}
                                                            />
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                        <div className={styles.replyAuthor}>
                                            {selectedTicket.user_name} · {formatDate(selectedTicket.created_at)}
                                        </div>
                                    </div>
                                )}

                                {!selectedTicket.replies?.length ? (
                                    <div className={styles.noReplies}>No replies yet. Be the first to respond!</div>
                                ) : (
                                    selectedTicket.replies.map(reply => {
                                        const isMine = reply.author_id === user?.id;
                                        const isAck = reply.message === 'User acknowledged.';
                                        if (isAck) {
                                            return (
                                                <div key={reply.id} className={styles.ackRow}>
                                                    <div className={styles.ackBubble}>
                                                        <CheckCircle2 size={13} />
                                                        User acknowledged
                                                    </div>
                                                    <div className={styles.replyAuthor}>
                                                        {formatDate(reply.created_at)}
                                                    </div>
                                                </div>
                                            );
                                        }
                                        return (
                                            <div key={reply.id} className={`${styles.replyRow} ${isMine ? styles.replyMine : styles.replyTheirs}`}>
                                                <div className={styles.replyBubble}>
                                                    <p>{reply.message}</p>
                                                </div>
                                                <div className={styles.replyAuthor}>
                                                    {isMine ? 'You' : reply.author_name} · {formatDate(reply.created_at)}
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>

                        {/* Reply input */}
                        <div className={styles.replyArea}>
                            <textarea
                                className={styles.replyTextarea}
                                placeholder="Type your reply..."
                                value={replyText}
                                onChange={e => setReplyText(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter' && e.metaKey) handleSendReply(); }}
                                rows={2}
                            />
                            <button
                                className={styles.sendBtn}
                                onClick={handleSendReply}
                                disabled={sendingReply || !replyText.trim()}
                            >
                                {sendingReply ? <div className={styles.btnSpinner} /> : <Send size={14} />}
                                {sendingReply ? 'Sending...' : 'Reply'}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default SupportAdmin;
